(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class GuVueExperience extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.initTimer) return;
      const schedule = () => {
        this.initTimer = window.setTimeout(() => {
          this.initTimer = undefined;
          this.initialize();
        }, 0);
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', schedule, { once: true });
        return;
      }
      schedule();
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.designMode = this.dataset.designMode === 'true' || Boolean(this.closest('[data-design-mode="true"]'));
      this.defaultSplit = Number(this.dataset.defaultSplit || 50);
      this.series = [...this.querySelectorAll('[data-series]')];
      this.activeSeries = this.querySelector('[data-series].is-active') || this.series[0];
      this.initialHandle = this.activeSeries?.dataset.handle;
      this.translations = this.readJSON('[data-guvue-translations]');
      this.drawerLabels = this.querySelector('[data-guvue-drawer-labels]')?.dataset || {};
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.userHasCompared = false;
      this.lastFocus = null;

      this.currentSeriesLabel = this.querySelector('[data-current-series]');
      this.slideTitle = this.querySelector('[data-slide-title]');
      this.slideDescription = this.querySelector('[data-slide-description]');
      this.showingLabel = this.querySelector('[data-showing-label]');
      this.purchaseSummary = this.querySelector('[data-purchase-summary]');
      this.comparePrice = this.querySelector('[data-compare-price-display]');
      this.buyButton = this.querySelector('[data-buy]');
      this.buyLabel = this.querySelector('[data-buy-label]');
      this.dots = this.querySelector('[data-dots]');
      this.status = this.querySelector('[data-a11y-status]');
      this.swipeHint = this.querySelector('[data-swipe-hint]');
      this.seriesDialog = this.querySelector('[data-series-dialog]');
      this.productDrawer = this.querySelector('[data-product-drawer]');

      this.setupComparisons();
      this.setupControls();
      this.activateSeries(this.activeSeries, { initial: true, updateHistory: false });
      this.setupHistory();
    }

    disconnectedCallback() {
      clearTimeout(this.initTimer);
      this.abortController?.abort();
      this.slideObserver?.disconnect();
      cancelAnimationFrame(this.demoFrame);
      clearTimeout(this.hintTimer);
    }

    readJSON(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '{}');
      } catch (_error) {
        return {};
      }
    }

    setupControls() {
      const options = { signal: this.signal };

      this.querySelector('[data-open-series]')?.addEventListener('click', () => {
        this.emit('collection_switcher_open');
        this.openOverlay(this.seriesDialog, this.querySelector('[data-open-series]'));
        this.querySelector('[data-open-series]')?.setAttribute('aria-expanded', 'true');
      }, options);

      this.querySelectorAll('[data-close-series]').forEach((button) => {
        button.addEventListener('click', () => this.closeSeriesDialog(), options);
      });

      this.querySelectorAll('[data-select-series]').forEach((button) => {
        button.addEventListener('click', () => {
          const next = this.series.find((item) => item.dataset.handle === button.dataset.selectSeries);
          if (next && next !== this.activeSeries) {
            this.activateSeries(next, { updateHistory: true });
            this.emit('collection_switch', { collection: next.dataset.handle });
          }
          this.closeSeriesDialog();
        }, options);
      });

      this.buyButton?.addEventListener('click', () => {
        if (!this.activeSeries || this.activeSeries.dataset.available !== 'true') return;
        this.emit('buy_click', { collection: this.activeSeries.dataset.handle });
        if (this.dataset.drawerEnabled === 'true') {
          this.populateDrawer();
          this.openOverlay(this.productDrawer, this.buyButton);
          this.emit('product_drawer_open', { collection: this.activeSeries.dataset.handle });
        } else {
          this.submitCheckout();
        }
      }, options);

      this.querySelectorAll('[data-close-drawer]').forEach((button) => {
        button.addEventListener('click', () => this.closeOverlay(this.productDrawer), options);
      });

      this.querySelector('[data-checkout-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitCheckout();
      }, options);

      this.addEventListener('keydown', (event) => {
        const overlay = [this.seriesDialog, this.productDrawer].find((item) => item && !item.hidden);
        if (!overlay) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          overlay === this.seriesDialog ? this.closeSeriesDialog() : this.closeOverlay(overlay);
          return;
        }
        if (event.key === 'Tab') this.trapFocus(event, overlay);
      }, options);
    }

    setupComparisons() {
      this.querySelectorAll('[data-comparison]').forEach((comparison) => {
        const handle = comparison.querySelector('[data-divider-handle]');
        let gesture = null;

        ['dragstart', 'selectstart', 'contextmenu'].forEach((eventName) => {
          comparison.addEventListener(eventName, (event) => event.preventDefault(), { signal: this.signal });
        });

        const updateFromPoint = (clientX, userInitiated = false) => {
          const rect = comparison.getBoundingClientRect();
          const value = clamp(((clientX - rect.left) / rect.width) * 100, 8, 92);
          this.setSplit(comparison, value);
          if (userInitiated) this.noteComparisonInteraction();
        };

        comparison.addEventListener('pointerdown', (event) => {
          if (!comparison.classList.contains('is-ready')) return;
          if (!event.target.closest('[data-divider-handle]')) return;
          gesture = {
            id: event.pointerId,
            startX: event.clientX,
            startedAt: performance.now()
          };
          event.preventDefault();
          comparison.setPointerCapture?.(event.pointerId);
          updateFromPoint(event.clientX, true);
        }, { signal: this.signal });

        comparison.addEventListener('pointermove', (event) => {
          if (!gesture || event.pointerId !== gesture.id) return;
          event.preventDefault();
          updateFromPoint(event.clientX, true);
        }, { signal: this.signal });

        const finishGesture = (event) => {
          if (!gesture || event.pointerId !== gesture.id) return;
          this.emit('comparison_drag_complete', {
            duration_ms: Math.round(performance.now() - gesture.startedAt)
          });
          gesture = null;
        };

        comparison.addEventListener('pointerup', finishGesture, { signal: this.signal });
        comparison.addEventListener('pointercancel', finishGesture, { signal: this.signal });

        handle?.addEventListener('keydown', (event) => {
          const current = Number(handle.getAttribute('aria-valuenow') || this.defaultSplit);
          let next = current;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= event.shiftKey ? 10 : 2;
          if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += event.shiftKey ? 10 : 2;
          if (event.key === 'Home') next = 8;
          if (event.key === 'End') next = 92;
          if (next === current) return;
          event.preventDefault();
          this.setSplit(comparison, next);
          this.noteComparisonInteraction();
        }, { signal: this.signal });
      });
    }

    setSplit(comparison, value) {
      const rounded = Math.round(clamp(value, 8, 92));
      comparison.style.setProperty('--split', `${value}%`);
      const handle = comparison.querySelector('[data-divider-handle]');
      const afterLabel = comparison.closest('[data-showcase]')?.dataset.afterLabel || '';
      handle?.setAttribute('aria-valuenow', String(rounded));
      handle?.setAttribute('aria-valuetext', this.template(this.translations.value, { VALUE: rounded, AFTER: afterLabel }));
    }

    noteComparisonInteraction() {
      cancelAnimationFrame(this.demoFrame);
      this.demoCancelled = true;
      this.hideSwipeHint();
      try { sessionStorage.setItem('guvue-comparison-demo', 'seen'); } catch (_error) {}
      if (!this.userHasCompared) {
        this.userHasCompared = true;
        this.emit('comparison_first_drag');
      }
    }

    activateSeries(series, { initial = false, updateHistory = false } = {}) {
      if (!series) return;
      this.slideObserver?.disconnect();
      this.demoCancelled = true;
      cancelAnimationFrame(this.demoFrame);

      this.series.forEach((item) => {
        const active = item === series;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
        item.querySelectorAll('[data-divider-handle]').forEach((handle) => { handle.tabIndex = -1; });
      });

      this.activeSeries = series;
      this.activeScroller = series.querySelector('[data-series-scroller]');
      this.activeSlides = [...series.querySelectorAll('[data-showcase]')];
      this.activeScroller?.scrollTo({ top: 0, behavior: 'auto' });
      this.currentSeriesLabel && (this.currentSeriesLabel.textContent = series.dataset.title || '');
      this.updatePurchase(series);
      this.buildDots();
      this.observeSlides();
      this.setActiveSlide(0, { announce: !initial });
      const firstLoad = this.loadPair(this.activeSlides[0]);
      if (this.activeSlides[1]) this.loadPair(this.activeSlides[1]);
      Promise.resolve(firstLoad).then(() => {
        if (initial) this.emit('first_screen_ready', { collection: series.dataset.handle });
        this.maybeRunFirstVisitCues();
      });

      this.querySelectorAll('[data-select-series]').forEach((button) => {
        const current = button.dataset.selectSeries === series.dataset.handle;
        button.classList.toggle('is-current', current);
        button.setAttribute('aria-current', current ? 'true' : 'false');
      });

      if (updateHistory && !this.designMode && series.dataset.productUrl) {
        history.pushState({ guvueSeries: series.dataset.handle }, '', series.dataset.productUrl);
      }
    }

    updatePurchase(series) {
      const available = series.dataset.available === 'true';
      const count = series.dataset.lutCount || '0';
      const price = series.dataset.price || '';
      if (this.purchaseSummary) {
        this.purchaseSummary.textContent = this.template(this.translations.summary, { COUNT: count, PRICE: price });
      }
      if (this.comparePrice) {
        this.comparePrice.textContent = series.dataset.comparePrice || '';
        this.comparePrice.hidden = !series.dataset.comparePrice;
      }
      if (this.buyButton) this.buyButton.disabled = !available;
      if (this.buyLabel) this.buyLabel.textContent = available ? this.translations.buy : this.translations.soldOut;
      const input = this.querySelector('[data-checkout-variant]');
      if (input) input.value = series.dataset.variantId || '';
      const priceNode = this.querySelector('[data-checkout-price]');
      if (priceNode) priceNode.textContent = price;
    }

    observeSlides() {
      if (!this.activeScroller || !this.activeSlides.length) return;
      this.slideObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible || visible.intersectionRatio < 0.55) return;
        const index = this.activeSlides.indexOf(visible.target);
        if (index !== this.activeIndex) this.setActiveSlide(index, { announce: true });
      }, { root: this.activeScroller, threshold: [0.55, 0.72, 0.9] });
      this.activeSlides.forEach((slide) => this.slideObserver.observe(slide));
    }

    setActiveSlide(index, { announce = false } = {}) {
      const slide = this.activeSlides?.[index];
      if (!slide) return;
      this.activeIndex = index;
      if (this.slideTitle) this.slideTitle.textContent = slide.dataset.title || '';
      if (this.slideDescription) this.slideDescription.textContent = slide.dataset.description || '';
      if (this.showingLabel) {
        const subtitle = slide.dataset.subtitle || '';
        this.showingLabel.textContent = subtitle;
        this.showingLabel.hidden = !subtitle;
      }
      this.activeSlides.forEach((item, itemIndex) => {
        item.querySelectorAll('[data-divider-handle]').forEach((handle) => { handle.tabIndex = itemIndex === index ? 0 : -1; });
      });
      this.querySelectorAll('[data-dot]').forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
      const positionText = this.template(this.translations.position, {
        CURRENT: index + 1,
        TOTAL: this.activeSlides.length
      });
      this.dots?.setAttribute('aria-label', positionText);
      if (announce && this.status) this.status.textContent = `${slide.dataset.title}. ${positionText}`;
      if (index > 0) {
        this.hideSwipeHint();
        if (!this.hasVerticalEvent) {
          this.hasVerticalEvent = true;
          this.emit('showcase_vertical_swipe');
        }
      }
      this.loadPair(slide);
      if (this.activeSlides[index + 1]) this.loadPair(this.activeSlides[index + 1]);
      if (this.activeSlides[index - 1]) this.loadPair(this.activeSlides[index - 1]);
      this.emit('showcase_view', { index: index + 1, collection: this.activeSeries.dataset.handle });
    }

    buildDots() {
      if (!this.dots) return;
      this.dots.replaceChildren();
      this.activeSlides.forEach((_slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `guvue-dot${index === 0 ? ' is-active' : ''}`;
        button.dataset.dot = '';
        button.setAttribute('aria-label', this.template(this.translations.goTo, { INDEX: index + 1 }));
        button.addEventListener('click', () => {
          this.activeSlides[index].scrollIntoView({ block: 'start', behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
        }, { signal: this.signal });
        this.dots.append(button);
      });
      this.dots.hidden = this.activeSlides.length < 2;
    }

    async loadPair(slide) {
      const comparison = slide?.querySelector('[data-comparison]');
      if (!comparison) return;
      if (comparison.loadPromise) return comparison.loadPromise;

      comparison.loadPromise = (async () => {
        const images = [...comparison.querySelectorAll('img[data-guvue-src]')];
        images.forEach((image) => {
          image.closest('picture')?.querySelectorAll('source[data-guvue-srcset]').forEach((source) => {
            source.srcset = source.dataset.guvueSrcset;
            source.sizes = source.dataset.guvueSizes || '100vw';
          });
          image.srcset = image.dataset.guvueSrcset || '';
          image.sizes = image.dataset.guvueSizes || '100vw';
          image.src = image.dataset.guvueSrc;
        });

        await Promise.all(images.map((image) => new Promise((resolve) => {
          if (image.complete && image.naturalWidth > 0) {
            resolve();
            return;
          }
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        })));

        await Promise.all(images.map((image) => {
          if (typeof image.decode !== 'function') return Promise.resolve();
          return image.decode().catch(() => undefined);
        }));
        comparison.classList.add('is-ready');
      })();

      return comparison.loadPromise;
    }

    maybeRunFirstVisitCues() {
      if (this.activeIndex !== 0) return;
      const comparison = this.activeSlides[0]?.querySelector('[data-comparison]');
      if (!comparison) return;

      let demoSeen = false;
      let hintSeen = false;
      try {
        demoSeen = sessionStorage.getItem('guvue-comparison-demo') === 'seen';
        hintSeen = sessionStorage.getItem('guvue-swipe-hint') === 'seen';
      } catch (_error) {}

      if (this.dataset.autoDemo === 'true' && !this.designMode && !this.prefersReducedMotion() && !demoSeen) {
        this.demoCancelled = false;
        try { sessionStorage.setItem('guvue-comparison-demo', 'seen'); } catch (_error) {}
        this.animateDemo(comparison);
      }

      if (this.dataset.swipeHint === 'true' && this.activeSlides.length > 1 && !this.designMode && !hintSeen) {
        this.hintTimer = setTimeout(() => {
          this.swipeHint?.classList.add('is-visible');
          try { sessionStorage.setItem('guvue-swipe-hint', 'seen'); } catch (_error) {}
        }, 850);
      }
    }

    animateDemo(comparison) {
      const startedAt = performance.now();
      const duration = 2600;
      this.setSplit(comparison, 38);
      const ease = (progress) => 1 - Math.pow(1 - progress, 3);
      const tick = (now) => {
        if (this.demoCancelled) return;
        const progress = clamp((now - startedAt) / duration, 0, 1);
        let value;
        if (progress < 0.72) {
          value = 38 + 20 * ease(progress / 0.72);
        } else {
          value = 58 - 8 * ease((progress - 0.72) / 0.28);
        }
        this.setSplit(comparison, value);
        if (progress < 1) this.demoFrame = requestAnimationFrame(tick);
      };
      this.demoFrame = requestAnimationFrame(tick);
    }

    hideSwipeHint() {
      clearTimeout(this.hintTimer);
      this.swipeHint?.classList.remove('is-visible');
    }

    populateDrawer() {
      const series = this.activeSeries;
      if (!series) return;
      const title = this.querySelector('[data-drawer-title]');
      const positioning = this.querySelector('[data-drawer-positioning]');
      const specs = this.querySelector('[data-specs]');
      const links = this.querySelector('[data-drawer-links]');
      const input = this.querySelector('[data-checkout-variant]');
      const checkoutButton = this.querySelector('[data-checkout-button]');
      const checkoutPrice = this.querySelector('[data-checkout-price]');

      if (title) title.textContent = series.dataset.title || '';
      if (positioning) positioning.textContent = series.dataset.positioning || '';
      if (input) input.value = series.dataset.variantId || '';
      if (checkoutButton) checkoutButton.disabled = series.dataset.available !== 'true';
      if (checkoutPrice) checkoutPrice.textContent = series.dataset.price || '';

      const values = [
        ['devices', this.drawerLabels.devices],
        ['profiles', this.drawerLabels.profiles],
        ['software', this.drawerLabels.software],
        ['formats', this.drawerLabels.formats],
        ['license', this.drawerLabels.license],
        ['included', this.drawerLabels.included]
      ];
      specs?.replaceChildren(...values.filter(([key]) => series.dataset[key]).map(([key, label]) => {
        const wrapper = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = series.dataset[key];
        wrapper.append(term, description);
        return wrapper;
      }));

      if (links) {
        links.replaceChildren();
        [
          [series.dataset.installationUrl, this.drawerLabels.installation],
          [series.dataset.compatibilityUrl, this.drawerLabels.compatibility]
        ].forEach(([url, label]) => {
          if (!url) return;
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.textContent = label;
          anchor.addEventListener('click', () => this.emit('detail_link_click', { label }), { signal: this.signal });
          links.append(anchor);
        });
      }
    }

    async submitCheckout() {
      const series = this.activeSeries;
      if (!series || series.dataset.available !== 'true') return;
      const button = this.querySelector('[data-checkout-button]');
      const label = this.querySelector('[data-checkout-label]');
      const error = this.querySelector('[data-checkout-error]');
      const fallbackForm = this.querySelector('[data-checkout-form]');
      if (button) button.disabled = true;
      if (label) label.textContent = this.translations.adding;
      if (error) error.hidden = true;
      this.emit('checkout_click', { collection: series.dataset.handle });

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(series.dataset.variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        this.emit('checkout_start', { collection: series.dataset.handle });
        const root = window.Shopify?.routes?.root || '/';
        window.location.assign(`${root}checkout`);
      } catch (_error) {
        if (button) button.disabled = false;
        if (label) label.textContent = this.translations.checkout;
        if (error) error.hidden = false;
        if (!window.fetch && fallbackForm) {
          fallbackForm.submit();
        } else if (this.productDrawer?.hidden) {
          this.populateDrawer();
          this.openOverlay(this.productDrawer, this.buyButton);
          if (error) error.hidden = false;
        }
      }
    }

    openOverlay(overlay, trigger) {
      if (!overlay) return;
      this.lastFocus = trigger || document.activeElement;
      overlay.hidden = false;
      this.classList.add('has-overlay');
      this.lockedScroller = this.activeScroller;
      if (this.lockedScroller) this.lockedScroller.dataset.previousOverflow = this.lockedScroller.style.overflowY || '';
      if (this.lockedScroller) this.lockedScroller.style.overflowY = 'hidden';
      requestAnimationFrame(() => this.getFocusable(overlay)[0]?.focus());
    }

    closeOverlay(overlay) {
      if (!overlay || overlay.hidden) return;
      overlay.hidden = true;
      this.classList.remove('has-overlay');
      if (this.lockedScroller) this.lockedScroller.style.overflowY = this.lockedScroller.dataset.previousOverflow || '';
      this.lockedScroller = null;
      this.lastFocus?.focus?.();
      this.lastFocus = null;
    }

    closeSeriesDialog() {
      this.closeOverlay(this.seriesDialog);
      this.querySelector('[data-open-series]')?.setAttribute('aria-expanded', 'false');
    }

    getFocusable(container) {
      return [...container.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => element.offsetParent !== null);
    }

    trapFocus(event, overlay) {
      const focusable = this.getFocusable(overlay);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setupHistory() {
      window.addEventListener('popstate', (event) => {
        const handle = event.state?.guvueSeries || this.initialHandle;
        const series = this.series.find((item) => item.dataset.handle === handle);
        if (series && series !== this.activeSeries) this.activateSeries(series, { updateHistory: false });
      }, { signal: this.signal });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    template(value = '', replacements = {}) {
      return Object.entries(replacements).reduce(
        (result, [key, replacement]) => result.replaceAll(`__${key}__`, String(replacement)),
        value
      );
    }

    emit(name, detail = {}) {
      const payload = { event: `guvue_${name}`, ...detail };
      this.dispatchEvent(new CustomEvent(`guvue:${name}`, { bubbles: true, detail: payload }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    }
  }

  if (!customElements.get('guvue-experience')) {
    customElements.define('guvue-experience', GuVueExperience);
  }
})();
